import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Badge, Button, Modal } from 'react-bootstrap';

import api from '../services/api'; 
import { 
    FaHistory, 
    FaChartLine, 
    FaTools, 
    FaLock, 
    FaCreditCard, 
    FaUserClock, 
    FaWallet, 
    FaFingerprint, 
    FaCheckCircle, 
    FaExclamationTriangle, 
    FaArrowUp, 
    FaArrowDown 
} from 'react-icons/fa';

const TransactionHistory = ({ setCurrentView, orderToPay, setOrderToPay }) => {
    const [orders, setOrders] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [showQR, setShowQR] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);

    const handleCloseQR = () => {
        setShowQR(false);
        setPaymentData(null);
        setHasAutoOpened(true); 
        if (typeof setOrderToPay === 'function') {
            setOrderToPay(null);
        }
    };

const handleOpenPayment = (order) => {
        if (!order) return;

        const stats = order.hubStats || {};
        const mt5Id = order.accountId || "NeXBot";
        
        // 1. Phí hợp tác 20% (Phải tính trước ở đây để biến này tồn tại)
        const rawFee = Math.max(0, stats.feeVND || 0);
        const fee20Percent = Math.round(rawFee * 0.20); 
        
        // 2. Phí cài đặt
        const setupFee = order.setupType === 'self_install' ? 0 : (order.totalPrice || 120000);
        
        // 3. Quyết định số tiền
        const isPricingFlow = order.status === 'pending';
        const totalAmountVnd = isPricingFlow ? setupFee : fee20Percent; // Sử dụng biến đã khai báo

        const isPricingZero = isPricingFlow && setupFee === 0;
        const isSetupFeeOnly = isPricingFlow && setupFee > 0;

        const description = isPricingZero 
            ? `Kich hoat IB ${mt5Id}` 
            : (isSetupFeeOnly ? `Phi cai dat MT5 ${mt5Id}` : `Phi hop tac 20pt ${mt5Id}`);

        setPaymentData({
            orderId: order._id,
            amountVnd: totalAmountVnd,
            description: description,
            accountId: mt5Id,
            isPricingZero: isPricingZero,
            isSetupFeeOnly: isSetupFeeOnly,
            qrUrl: totalAmountVnd > 0 
                ? `https://img.vietqr.io/image/MB-39394947862-compact2.png?amount=${totalAmountVnd}&addInfo=${encodeURIComponent(description)}&accountName=TRAN%20VAN%20CONG`
                : null
        });
        
        setShowQR(true);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        console.log("🚀 Đang gọi API: /payment/my-orders"); 
        try {
            const res = await api.get('/api/order/my-orders'); 
            if (res.data.success) {
                console.log("✅ Dữ liệu nhận được:", res.data.orders);
                setOrders(res.data.orders);
            }
        } catch (err) {
            console.error("❌ Lỗi khi tải lịch sử:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // 🛡️ VÁ LỖI 1: Sửa logic tự động mở Modal, chỉ cho phép mở tự động khi orderToPay thực sự có chủ đích từ luồng ngoài truyền vào cụ thể
    useEffect(() => {
        if (orderToPay && !hasAutoOpened && orders.length > 0) {
            const targetId = typeof orderToPay === 'object' ? orderToPay._id : orderToPay;
            if (targetId) {
                const match = orders.find(o => o._id === targetId || o.accountId === targetId);
                if (match && !showQR) {
                    handleOpenPayment(match);
                    setHasAutoOpened(true);
                }
            }
        }
    }, [orderToPay, orders, hasAutoOpened, showQR]);

    const handleFinalConfirm = async () => {
        if (!paymentData || !paymentData.orderId) {
            alert("Không tìm thấy thông tin đơn hàng!");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await api.post('/api/order/confirm-payment-sent', {
                orderId: paymentData.orderId,
                amountVnd: paymentData.amountVnd, 
                note: paymentData.description,    
                paymentType: 'profit_sharing',
                status: 'waiting_confirm' 
            });

            if (response.data && response.data.success) { 
                alert("Đã gửi xác nhận phí hợp tác! Vui lòng chờ Admin duyệt để mở khóa tài khoản.");
                setShowQR(false);
                if (typeof fetchData === 'function') {
                    fetchData();
                }
            } else {
                alert("Lỗi: " + (response.data?.message || "Không thể gửi xác nhận"));
            }
        } catch (error) {
            console.error("❌ Lỗi luồng 20%:", error);
            alert("Lỗi kết nối server. Vui lòng chụp màn hình bill và gửi Zalo hỗ trợ!");
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- PHÂN LOẠI ĐƠN HÀNG THÔNG MINH ---
    const setupFeeOrders = orders.filter(o => 
        o.status === 'pending' || o.status === 'waiting_confirm'
    );

    const activeProfitSharing = orders.filter(o => 
        (o.status === 'active' || o.status === 'locked' || o.status === 'stop') && o.accountId
    );

    if (loading && orders.length === 0) {
        return (
            <div className="text-center my-5 text-light">
                <Spinner animation="border" variant="warning" className="mb-2" />
                <p className="text-muted italic">Đang đồng bộ dữ liệu tài chính...</p>
            </div>
        );
    }

    return (
        <Container className="mt-4 pt-2 pb-5"> 
            {/* Header */}
            <Row className="justify-content-center mb-5 text-center">
                <Col md={11}>
                    <div className="p-4 rounded-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', border: '1px solid #334155' }}>
                        <h1 className="fw-bolder mb-2 text-uppercase" style={{ color: '#eab308', fontSize: '1.6rem', letterSpacing: '1px' }}>
                            <FaHistory className="me-3"/> LỊCH SỬ GIAO DỊCH & BILLING
                        </h1>
                        <div className="d-flex justify-content-center gap-3">
                            <Badge bg="dark" className="border border-secondary text-muted">Tỉ giá: 26.000 ₫</Badge>
                            <Badge bg="dark" className="border border-secondary text-muted">Update: Real-time</Badge>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* PHẦN 1: PHÍ KÍCH HOẠT */}
            {setupFeeOrders.length > 0 && (
                <Row className="justify-content-center mb-5">
                    <Col md={11}>
                        <h5 className="text-warning mb-3 d-flex align-items-center fw-bold"><FaTools className="me-2"/> PHÍ KÍCH HOẠT HỆ THỐNG</h5>
                        <Card className="shadow-lg border-0 rounded-4 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                            <Table responsive variant="dark" className="mb-0">
                                <thead>
                                    <tr style={{ backgroundColor: '#1e293b' }}>
                                        <th className="py-3 px-4">MT5 ID</th>
                                        <th>Loại Phí</th>
                                        <th>Thành Tiền</th>
                                        <th>Trạng Thái</th>
                                        <th className="text-center">Thao Tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {setupFeeOrders.map((order) => {
                                        const stats = order.hubStats || {};
                                        const setupFee = order.setupType === 'self_install' ? 0 : (order.totalPrice || 0);
                                        // 🛡️ VÁ LỖI 2 tại bảng kích hoạt nếu có: Chặn số âm cho phí hợp tác
                                        const sharingUsd = parseFloat(stats.total_profit_sharing) || 0;
                                        const sharingVnd = Math.max(0, Math.round(sharingUsd * 26500));

                                        return (
                                            <tr key={order._id} className="align-middle border-bottom border-secondary border-opacity-25">
                                                <td className="px-4 fw-bold text-info">{order.accountId || order.account_id}</td>
                                                <td className="text-muted small">
                                                    {setupFee > 0 && <div>• Phí cài đặt hệ thống</div>}
                                                    {sharingVnd > 0 && <div>• Phí hợp tác (20% LN)</div>}
                                                    {setupFee === 0 && sharingVnd === 0 && <span>Không phát sinh phí</span>}
                                                </td>
                                                <td className="fw-bold text-warning">
                                                    {setupFee === 0 ? "0 ₫" : `${setupFee.toLocaleString()} ₫`}
                                                </td>
                                                <td>
                                                    <Badge 
                                                        bg={order.is_pending_payment === 1 || order.status === 'pending' ? 'danger' : 'info'} 
                                                        className="text-uppercase"
                                                    >
                                                        {order.is_pending_payment === 1 || order.status === 'pending' ? 'Chờ thanh toán' : 'Đang xử lý'}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    {(order.status === 'pending' || order.is_pending_payment === 1) && (
                                                        <Button 
                                                            variant="outline-info" 
                                                            size="sm" 
                                                            className="fw-bold px-3 rounded-pill shadow-sm"
                                                            onClick={() => handleOpenPayment(order)}
                                                        >
                                                            <FaCheckCircle className="me-1"/> XÁC NHẬN IB PARTNER
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* PHẦN 2: THÔNG TIN TỪ SERVER 3001 */}
            <Row className="justify-content-center">
                <Col md={11}>
                    <h5 className="text-success mb-3 d-flex align-items-center fw-bold">
                        <FaChartLine className="me-2"/> CHI TIẾT TÀI CHÍNH & HWID LOCK
                    </h5>
                    <Card className="shadow-lg border-0 rounded-4 overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
                        <Table responsive variant="dark" className="mb-0 text-center">
                            <thead>
                                <tr style={{ backgroundColor: '#1e293b' }}>
                                    <th className="py-3 px-4 text-start">MT5 / HWID</th>
                                    <th>Số Dư & Vốn Gốc</th>
                                    <th>Nạp ($)</th>
                                    <th>Rút ($)</th>
                                    <th>Lợi Nhuận</th>
                                    <th>Phí hợp tác (20%)</th>
                                    <th>Trạng Thái</th>
                                    <th className="text-center">Thao Tác</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeProfitSharing.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-5 text-muted">
                                            Chưa có dữ liệu tài khoản từ hệ thống Hub.
                                        </td>
                                    </tr>
                                ) : (
                                    activeProfitSharing.map((order) => {
                                        const stats = order.hubStats || {};
                                        
                                       const getSafe = (order, fields) => {
    // Ưu tiên lấy từ hubStats trước
    if (order.hubStats) {
        for (let f of fields) {
            if (order.hubStats[f] !== undefined && order.hubStats[f] !== null) return parseFloat(order.hubStats[f]);
        }
    }
    // Nếu không có, tìm trong object đơn hàng gốc
    for (let f of fields) {
        if (order[f] !== undefined && order[f] !== null) return parseFloat(order[f]);
    }
    return 0;
};

        // Khi gọi trong map, bố cần đảm bảo truyền 'order' vào làm tham số đầu tiên cho mọi dòng
const balance = getSafe(order, ['current_balance', 'balance', 'account_balance']);
const profit = getSafe(order, ['current_profit', 'netProfit', 'profit']);
const startingBal = getSafe(order, ['starting_balance', 'initial_balance']);
const deposit = getSafe(order, ['total_deposit', 'deposit_amount']);
const withdraw = getSafe(order, ['total_withdraw', 'withdraw_amount']);

// 🛡️ VÁ LỖI: Phí hợp tác VNĐ không bao giờ bị số âm
const rawFeeVnd = getSafe(order, ['total_profit_sharing_vnd', 'feeVND', 'sharingVnd']);
const feeVnd = Math.max(0, rawFeeVnd);

// Lấy thông tin HWID từ stats hoặc order
const hwid = order.hubStats?.hwid_lock || order.hwid_lock || "Empty";

// Kiểm tra trạng thái khóa (ưu tiên kiểm tra trong hubStats nếu có)
const isLocked = order.status === 'locked' || (order.hubStats?.is_pending_payment === 1);

                                        return (
                                            <tr key={order._id || order.account_id} className="align-middle border-bottom border-secondary border-opacity-10">
                                                <td className="px-4 py-3 text-start">
                                                    <div className="fw-bold text-info">{order.accountId || order.account_id}</div>
                                                    <div className="text-muted mt-1" style={{ fontSize: '10px' }}>
                                                        <FaFingerprint className="me-1"/> {hwid.length > 12 ? hwid.substring(0, 12) + "..." : hwid}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="fw-bold text-light">${balance.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                                                    <div className="text-muted" style={{ fontSize: '10px' }}>Gốc: ${startingBal.toLocaleString()}</div>
                                                </td>
                                                <td className="text-center">
                                                    {deposit > 0 ? (
                                                        <div className="text-success small fw-bold" style={{ fontSize: '10px' }}>
                                                            <FaArrowUp size={8} className="me-1"/> +${deposit.toLocaleString()}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted opacity-50 small">-</span>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {withdraw > 0 ? (
                                                        <div className="text-danger small fw-bold" style={{ fontSize: '10px' }}>
                                                            <FaArrowDown size={8} className="me-1"/> -${withdraw.toLocaleString()}
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted opacity-50 small">-</span>
                                                    )}
                                                </td>
                                                <td className={profit >= 0 ? "text-success fw-bold" : "text-danger fw-bold"}>
                                                    {profit > 0 ? '+' : ''}${profit.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                                </td>
                                                <td className="text-warning fw-bold">
                                                    {new Intl.NumberFormat('vi-VN').format(Math.round(feeVnd))} VND
                                                </td>
                                                <td>
                                                    <Badge bg={isLocked ? "danger" : "success"} className="rounded-pill px-2" style={{ fontSize: '9px' }}>
                                                        {isLocked ? "ĐÃ KHÓA" : "HOẠT ĐỘNG"}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    {order.status === 'waiting_confirm' ? (
                                                        <button disabled className="btn btn-sm btn-dark rounded-pill px-3 opacity-50" style={{ pointerEvents: 'none' }}>
                                                            <Spinner size="sm" className="me-1" /> CHỜ DUYỆT
                                                        </button>
                                                    ) : (
                                                        <Button 
                                                            variant={isLocked ? "danger" : "outline-warning"} 
                                                            size="sm" 
                                                            className="rounded-pill px-3 fw-bold"
                                                            onClick={() => !order.isBillingFrozen && handleOpenPayment(order)}
                                                            disabled={order.isBillingFrozen}
                                                        >
                                                            {isLocked ? "THANH TOÁN" : "BILLING"}
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                        <div className="p-3 bg-black bg-opacity-20 text-muted italic border-top border-secondary border-opacity-10" style={{ fontSize: '11px' }}>
                            * Hệ thống tự động ghi nhận các khoản Nạp/Rút để tính toán Lợi Nhuận Ròng chính xác. <br/>
                            Lợi nhuận ròng = (Số dư hiện tại) - (Vốn gốc) - (Tổng nạp - Tổng rút).
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Modal QR Code */}
            <Modal show={showQR} onHide={handleCloseQR} centered contentClassName="bg-dark text-light border-0 shadow-lg rounded-4">
                <Modal.Header closeButton closeVariant="white" className="border-bottom border-secondary border-opacity-25 px-4">
                    <Modal.Title className="text-warning fw-bold d-flex align-items-center">
                        <FaCreditCard className="me-2"/> XÁC NHẬN THANH TOÁN
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="text-center p-4">
                    {(paymentData?.isPricingZero || paymentData?.isSetupFeeOnly) && (
                        <div className="alert alert-info small border-0 bg-info bg-opacity-10 text-info mb-4 text-start">
                            <FaExclamationTriangle className="me-2"/>
                            <strong>Lưu ý:</strong> Bạn cần cài đặt tài khoản theo link IB của NeXBot để được kích hoạt sử dụng BOT.
                        </div>
                    )}

                    {paymentData?.isPricingZero ? (
                        <div className="py-2">
                            <FaCheckCircle className="text-info mb-3" style={{ fontSize: '3rem' }} />
                            <h5 className="text-light">Xác nhận kích hoạt miễn phí</h5>
                            <p className="text-muted small">Tài khoản MT5: <b>{paymentData.accountId}</b></p>
                            <div className="bg-black bg-opacity-50 p-3 rounded-3 border border-secondary mb-3">
                                <span className="text-muted fw-bold small">THÀNH TIỀN: </span>
                                <span className="fw-bold text-info fs-4">0 ₫</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="text-muted small mb-3 text-uppercase tracking-wider">Quét mã VietQR để thanh toán</p>
                            {paymentData?.qrUrl ? (
                                <div className="bg-white p-3 rounded-4 d-inline-block mb-4 shadow-sm border border-4 border-white">
                                    <img 
                                        src={paymentData.qrUrl} 
                                        alt="VietQR" 
                                        style={{ width: '220px', height: '220px', objectFit: 'contain' }} 
                                    />
                                </div>
                            ) : (
                                <div className="py-4"><Spinner variant="warning" /></div>
                            )}

                            <div className="text-start bg-black bg-opacity-50 p-3 rounded-3 border border-secondary mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className="text-muted fw-bold small d-block">TỔNG CẦN TRẢ:</span>
                                        <span className="text-secondary small">{paymentData?.description}</span>
                                    </div>
                                    <span className="fw-bold text-warning fs-3">
                                        {Number(paymentData?.amountVnd || 0).toLocaleString()} ₫
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-0 px-4 pb-4">
                    <Button 
                        variant={paymentData?.isPricingZero ? "info" : "success"} 
                        className="fw-bold w-100 py-3 shadow rounded-3 text-uppercase border-0" 
                        onClick={handleFinalConfirm} 
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <><Spinner size="sm" className="me-2" /> Đang gửi...</>
                        ) : (
                            paymentData?.isPricingZero ? "Gửi yêu cầu kích hoạt bot" : "Tôi đã chuyển tiền thành công"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Style hiệu ứng */}
            <style>{`
                .pulse-red { animation: pulse-red-effect 1.5s infinite; border: 1px solid #ff4d4d; }
                @keyframes pulse-red-effect {
                    0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
                    70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
                }
                .border-dashed { border-style: dashed !important; }
                .font-mono { font-family: 'Courier New', Courier, monospace; }
                .table-dark { --bs-table-bg: transparent; }
                tr:hover { background-color: rgba(255, 255, 255, 0.03) !important; transition: 0.3s; }
                button:disabled {
                    background: #334155 !important; 
                    color: #94a3b8 !important;     
                    border: 1px solid #475569 !important;
                    cursor: not-allowed !important;
                    pointer-events: none; 
                    filter: grayscale(1);  
                }
            `}
            </style>
        </Container>
    );
};

export default TransactionHistory;